import { Spinner } from "@/components/ui/spinner";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { TransactionSource } from "@boo/shared";
import type { Transaction } from "@boo/shared";
import type { UpdateTransactionPayload } from "../../../../services/expenseApi";
import AmountInput from "@/components/AmountInput";

interface EditIncomeModalProps {
  transaction: Transaction | null;
  onClose: () => void;
  onSave: (id: string, payload: UpdateTransactionPayload) => Promise<void>;
}

function formatBaht(n: number) {
  return `฿${n.toLocaleString("en-US")}`;
}

export default function EditIncomeModal({
  transaction,
  onClose,
  onSave,
}: EditIncomeModalProps) {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (transaction) {
      setTitle(transaction.title);
      setAmount(String(transaction.amount));
      setNote(transaction.note ?? "");
      setDate(transaction.date);
    }
    setError(null);
  }, [transaction]);

  if (!transaction) return null;

  const isRecurring = transaction.source === TransactionSource.RecurringIncome;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amountNum = parseFloat(amount);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("Amount must be greater than 0.");
      return;
    }

    const payload: UpdateTransactionPayload = {};
    if (amountNum !== transaction!.amount) payload.amount = amountNum;
    if (title.trim() !== transaction!.title) payload.title = title.trim();
    if ((note ?? "") !== (transaction!.note ?? "")) payload.note = note;
    if (date !== transaction!.date) payload.date = date;

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(transaction!.id, payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[15px] font-semibold text-foreground">
            Edit income
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Title */}
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-muted-foreground font-medium">
              Title
            </label>
            <input
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Amount */}
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-muted-foreground font-medium">
              Amount (฿)
            </label>
            <AmountInput
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border [font-variant-numeric:tabular-nums]"
              value={amount}
              onChange={setAmount}
            />
            {((amount && !isNaN(parseFloat(amount))) || isRecurring) && (
              <div className="flex flex-col gap-2 pt-0.5">
                {amount && !isNaN(parseFloat(amount)) && (
                  <span className="text-[11px] text-muted-foreground">
                    {formatBaht(parseFloat(amount))}
                  </span>
                )}
                {isRecurring && (
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Changing amount or title will also update this recurring
                    income for future months.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Note */}
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-muted-foreground font-medium">
              Note
            </label>
            <input
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
              placeholder="Optional"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {/* Date */}
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-muted-foreground font-medium">
              Date
            </label>
            <input
              type="date"
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-border [font-variant-numeric:tabular-nums]"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {error && <p className="text-[12px] text-red-500">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="mt-1 inline-flex items-center justify-center gap-1.5 w-full rounded-[8px] bg-foreground text-[var(--page-bg)] py-3 text-[13px] font-semibold hover:opacity-80 transition-opacity disabled:opacity-40 cursor-pointer"
          >
            {saving && <Spinner className="size-3.5" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}
